/**
 * Group detail (F1): members with a ROLE (incl. cell moderator) and the
 * CAPABILITIES its category enables — music (songs + rehearsals +
 * setlists with per-occasion key) and meetings/attendance (cell). The
 * system category decides which sections exist; the name was set by the
 * church (GroupType).
 */
import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import {
  effectiveKey,
  getChurchesQuery,
  getGroupCategoryCapabilitiesQuery,
  getGroupMembersQuery,
  getGroupsQuery,
  getGroupTypesQuery,
  getMeetingsQuery,
  getPeopleQuery,
  getRehearsalsQuery,
  getSetlistSongsQuery,
  getSetlistsQuery,
  getSongsQuery,
  MUSICAL_KEYS,
  orderSetlistSongs,
  parseInstant,
  queryKeys,
} from "@agape/shared";
import {
  type ChurchGroupRecord,
  type ChurchRecord,
  eq,
  group_member,
  type GroupCategoryCapabilityRecord,
  type GroupMemberRecord,
  type GroupTypeRecord,
  type MeetingRecord,
  type PersonRecord,
  type RehearsalRecord,
  rehearsal as rehearsalTable,
  setlist_song,
  type SetlistRecord,
  type SetlistSongRecord,
  setlist as setlistTable,
  type SongRecord,
  song as songTable,
} from "@agape/powersync";
import { CalendarClock, ListMusic, Music, Plus, Trash2, UserPlus } from "@fleet/icons";

import { Link } from "@tanstack/solid-router";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@fleet/ui/card";
import { PageSubHeader } from "@/components/ui/page-subheader";
import { Select } from "@/components/ui/select";
import { showToast } from "@/components/ui/toast";
import { Field, FormDialog } from "./form";
import { CleaningSection } from "./cleaning-section";
import { DevotionalSection } from "./devotional-section";
import { SceneSection } from "./scene-section";
import {
  formatWhen,
  localInputToIso,
  newId,
  nowIso,
  useDomainQuery,
  useGrants,
  useWriter,
} from "../use-domain";

export function GroupDetailPage(props: { groupId: string }) {
  const { t } = useI18n();
  const write = useWriter();
  const grants = useGrants();

  const groups = useDomainQuery<ChurchGroupRecord>(
    (uid) => queryKeys.domain.groups(uid),
    (db) => getGroupsQuery(db),
  );
  const types = useDomainQuery<GroupTypeRecord>(
    (uid) => queryKeys.domain.groupTypes(uid),
    (db) => getGroupTypesQuery(db),
  );
  const categoryCaps = useDomainQuery<GroupCategoryCapabilityRecord>(
    () => [...queryKeys.domain.catalogs(), "categoryCaps"],
    (db) => getGroupCategoryCapabilitiesQuery(db),
  );
  const members = useDomainQuery<GroupMemberRecord>(
    (uid) => queryKeys.domain.groupMembers(uid),
    (db) => getGroupMembersQuery(db),
  );
  const people = useDomainQuery<PersonRecord>(
    (uid) => queryKeys.domain.people(uid),
    (db) => getPeopleQuery(db),
  );
  const songs = useDomainQuery<SongRecord>(
    (uid) => queryKeys.domain.songs(uid),
    (db) => getSongsQuery(db),
  );
  const rehearsals = useDomainQuery<RehearsalRecord>(
    (uid) => queryKeys.domain.rehearsals(uid),
    (db) => getRehearsalsQuery(db),
  );
  const setlists = useDomainQuery<SetlistRecord>(
    (uid) => queryKeys.domain.setlists(uid),
    (db) => getSetlistsQuery(db),
  );
  const setlistSongs = useDomainQuery<SetlistSongRecord>(
    (uid) => queryKeys.domain.setlistSongs(uid),
    (db) => getSetlistSongsQuery(db),
  );
  const meetings = useDomainQuery<MeetingRecord>(
    (uid) => queryKeys.domain.meetings(uid),
    (db) => getMeetingsQuery(db),
  );
  const churches = useDomainQuery<ChurchRecord>(
    (uid) => queryKeys.domain.churches(uid),
    (db) => getChurchesQuery(db),
  );

  const group = createMemo(() => (groups.data ?? []).find((g) => g.id === props.groupId));
  const church = createMemo(() => (churches.data ?? []).find((c) => c.id === group()?.church_id));
  const groupType = createMemo(() =>
    (types.data ?? []).find((ty) => ty.id === group()?.group_type_id),
  );
  const capabilities = createMemo(() =>
    (categoryCaps.data ?? [])
      .filter((cc) => cc.category_id === groupType()?.category_id)
      .map((cc) => cc.capability_id ?? ""),
  );
  const hasCap = (cap: string) => capabilities().includes(cap);
  const scope = () => [props.groupId, group()?.church_id];

  const myMembers = createMemo(() =>
    (members.data ?? []).filter((m) => m.group_id === props.groupId),
  );
  const mySongs = createMemo(() => (songs.data ?? []).filter((s) => s.group_id === props.groupId));
  const myRehearsals = createMemo(() =>
    (rehearsals.data ?? []).filter((r) => r.group_id === props.groupId),
  );
  const mySetlists = createMemo(() =>
    (setlists.data ?? []).filter((s) => s.group_id === props.groupId),
  );
  const myMeetings = createMemo(() =>
    (meetings.data ?? []).filter((m) => m.group_id === props.groupId),
  );

  // ── Members with roles ──────────────────────────────────────────────────
  const [memberOpen, setMemberOpen] = createSignal(false);
  const [memberPerson, setMemberPerson] = createSignal("");
  const [memberRole, setMemberRole] = createSignal("");
  // STABLE list (does not shrink when adding): a Select whose chosen option
  // disappears from the options leaves Kobalte with an orphan selection.
  const personOptions = createMemo(() =>
    (people.data ?? []).map((p) => ({ value: p.id, label: p.name ?? p.id })),
  );
  const addMember = async () => {
    const personId = memberPerson();
    const role = memberRole().trim() || null;
    if (!personId || myMembers().some((m) => m.person_id === personId)) {
      return;
    }
    // Reset the selection BEFORE inserting: the insert re-computes the
    // options (the added person leaves the list) and a Kobalte Select whose
    // selected option was removed crashes when re-rendering the Value.
    setMemberOpen(false);
    setMemberPerson("");
    setMemberRole("");
    await write().insert(group_member).values({
      id: newId(),
      church_id: group()?.church_id,
      group_id: props.groupId,
      person_id: personId,
      role,
      created_at: nowIso(),
      updated_at: nowIso(),
    });
    showToast(t(msg`Member added`), "success");
  };
  const removeMember = async (id: string) => {
    await write().delete(group_member).where(eq(group_member.id, id));
  };

  // ── Songs ─────────────────────────────────────────────────────────────────
  const [songOpen, setSongOpen] = createSignal(false);
  const [songTitle, setSongTitle] = createSignal("");
  const [songKey, setSongKey] = createSignal("C");
  const [songAuthor, setSongAuthor] = createSignal("");
  const [songTempo, setSongTempo] = createSignal("");
  const addSong = async () => {
    if (!songTitle().trim()) {
      return;
    }
    await write()
      .insert(songTable)
      .values({
        id: newId(),
        church_id: group()?.church_id,
        group_id: props.groupId,
        title: songTitle().trim(),
        author: songAuthor().trim() || null,
        song_key: songKey(),
        tempo: songTempo() ? Number(songTempo()) : null,
        created_at: nowIso(),
        updated_at: nowIso(),
      });
    setSongOpen(false);
    setSongTitle("");
    showToast(t(msg`Song added to the library`), "success");
  };

  // ── Rehearsals ───────────────────────────────────────────────────────────
  const [rehearsalOpen, setRehearsalOpen] = createSignal(false);
  const [rehearsalTitle, setRehearsalTitle] = createSignal("");
  const [rehearsalStart, setRehearsalStart] = createSignal("");
  const [rehearsalLocation, setRehearsalLocation] = createSignal("");
  const addRehearsal = async () => {
    const starts = localInputToIso(rehearsalStart());
    if (!rehearsalTitle().trim() || !starts) {
      return;
    }
    await write()
      .insert(rehearsalTable)
      .values({
        id: newId(),
        church_id: group()?.church_id,
        group_id: props.groupId,
        title: rehearsalTitle().trim(),
        starts_at: starts,
        location: rehearsalLocation().trim() || null,
        created_at: nowIso(),
        updated_at: nowIso(),
      });
    setRehearsalOpen(false);
    setRehearsalTitle("");
    setRehearsalStart("");
    showToast(t(msg`Rehearsal scheduled`), "success");
  };

  // ── Setlists ──────────────────────────────────────────────────────────────
  const [setlistOpen, setSetlistOpen] = createSignal(false);
  const [setlistTitle, setSetlistTitle] = createSignal("");
  const [expandedSetlist, setExpandedSetlist] = createSignal<string | null>(null);
  const [pickSong, setPickSong] = createSignal("");
  const [pickKey, setPickKey] = createSignal("");
  const addSetlist = async () => {
    if (!setlistTitle().trim()) {
      return;
    }
    await write().insert(setlistTable).values({
      id: newId(),
      church_id: group()?.church_id,
      group_id: props.groupId,
      title: setlistTitle().trim(),
      created_at: nowIso(),
      updated_at: nowIso(),
    });
    setSetlistOpen(false);
    setSetlistTitle("");
    showToast(t(msg`Setlist created`), "success");
  };
  const songsOf = (setlistId: string) =>
    orderSetlistSongs((setlistSongs.data ?? []).filter((ss) => ss.setlist_id === setlistId));
  const addSongToSetlist = async (setlistId: string) => {
    if (!pickSong()) {
      return;
    }
    await write()
      .insert(setlist_song)
      .values({
        id: newId(),
        church_id: group()?.church_id,
        group_id: props.groupId,
        setlist_id: setlistId,
        song_id: pickSong(),
        sort_order: songsOf(setlistId).length + 1,
        key_override: pickKey() || null,
        created_at: nowIso(),
        updated_at: nowIso(),
      });
    setPickSong("");
    setPickKey("");
  };
  const removeSetlistSong = async (id: string) => {
    await write().delete(setlist_song).where(eq(setlist_song.id, id));
  };
  const songTitleOf = (id: string | null) =>
    (songs.data ?? []).find((s) => s.id === id)?.title ?? id ?? "";
  const songKeyOf = (id: string | null) =>
    (songs.data ?? []).find((s) => s.id === id)?.song_key ?? null;

  return (
    <div class="space-y-6">
      <PageSubHeader title={group()?.name ?? t(msg`Group`)} backTo="/groups" />
      <Show
        when={group()}
        fallback={
          <p class="text-muted-foreground">{t(msg`This group is not synced to your account.`)}</p>
        }
      >
        {(g) => (
          <>
            <p class="text-sm text-muted-foreground" data-testid="group-caps">
              {groupType()?.name} · {t(msg`Capabilities`)}: {capabilities().join(", ")}
            </p>

            {/* ── Miembros con función ──────────────────────────────────── */}
            <section class="space-y-2">
              <div class="flex items-center justify-between">
                <h3 class="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  {t(msg`Members`)}
                </h3>
                <Show when={grants.can(scope(), "groups", "write")}>
                  <Button
                    size="sm"
                    variant="ghost"
                    data-testid="group-member-add"
                    onClick={() => setMemberOpen(true)}
                  >
                    <UserPlus size={14} /> {t(msg`Add member`)}
                  </Button>
                </Show>
              </div>
              <Show
                when={myMembers().length > 0}
                fallback={
                  <p class="text-sm text-muted-foreground" data-testid="group-members-empty">
                    {t(msg`No members yet.`)}
                  </p>
                }
              >
                <For each={myMembers()}>
                  {(m) => (
                    <Card data-testid="group-member-item">
                      <CardContent class="flex items-center justify-between gap-3 py-2.5">
                        <p class="min-w-0 truncate text-sm">
                          <span class="font-medium">{m.person_name ?? m.person_id}</span>
                          <Show when={m.role}>
                            <span class="text-muted-foreground"> · {m.role}</span>
                          </Show>
                        </p>
                        <Show when={grants.can(scope(), "groups", "write")}>
                          <Button
                            size="icon"
                            variant="ghost"
                            aria-label={t(msg`Remove member`)}
                            onClick={() => void removeMember(m.id)}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </Show>
                      </CardContent>
                    </Card>
                  )}
                </For>
              </Show>
            </section>

            {/* ── Música: canciones ─────────────────────────────────────── */}
            <Show when={hasCap("songs")}>
              <section class="space-y-2" data-testid="group-songs">
                <div class="flex items-center justify-between">
                  <h3 class="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    {t(msg`Songs`)}
                  </h3>
                  <Show when={grants.can(scope(), "songs", "write")}>
                    <Button
                      size="sm"
                      variant="ghost"
                      data-testid="song-create"
                      onClick={() => setSongOpen(true)}
                    >
                      <Music size={14} /> {t(msg`New song`)}
                    </Button>
                  </Show>
                </div>
                <Show
                  when={mySongs().length > 0}
                  fallback={
                    <p class="text-sm text-muted-foreground" data-testid="group-songs-empty">
                      {t(msg`No songs yet.`)}
                    </p>
                  }
                >
                  <For each={mySongs()}>
                    {(s) => (
                      <Link to="/songs/$songId" params={{ songId: s.id }} class="block">
                        <Card
                          data-testid="song-item"
                          data-song-id={s.id}
                          class="transition-colors hover:border-primary/40"
                        >
                          <CardContent class="flex items-center justify-between gap-3 py-2.5">
                            <p class="min-w-0 truncate text-sm font-medium">{s.title}</p>
                            <p class="shrink-0 text-xs text-muted-foreground">
                              {[s.song_key, s.tempo ? `${s.tempo} bpm` : null]
                                .filter(Boolean)
                                .join(" · ")}
                            </p>
                          </CardContent>
                        </Card>
                      </Link>
                    )}
                  </For>
                </Show>
              </section>
            </Show>

            {/* ── Música: ensayos ───────────────────────────────────────── */}
            <Show when={hasCap("rehearsals")}>
              <section class="space-y-2" data-testid="group-rehearsals">
                <div class="flex items-center justify-between">
                  <h3 class="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    {t(msg`Rehearsals`)}
                  </h3>
                  <Show when={grants.can(scope(), "rehearsals", "write")}>
                    <Button
                      size="sm"
                      variant="ghost"
                      data-testid="rehearsal-create"
                      onClick={() => setRehearsalOpen(true)}
                    >
                      <CalendarClock size={14} /> {t(msg`New rehearsal`)}
                    </Button>
                  </Show>
                </div>
                <Show
                  when={myRehearsals().length > 0}
                  fallback={
                    <p class="text-sm text-muted-foreground">{t(msg`No rehearsals yet.`)}</p>
                  }
                >
                  <For each={myRehearsals()}>
                    {(r) => (
                      <Card data-testid="rehearsal-item">
                        <CardContent class="flex items-center justify-between gap-3 py-2.5">
                          <p class="min-w-0 truncate text-sm font-medium">{r.title}</p>
                          <p class="shrink-0 text-xs text-muted-foreground">
                            {r.starts_at ? formatWhen(parseInstant(r.starts_at) ?? 0) : ""}
                            <Show when={r.location}> · {r.location}</Show>
                          </p>
                        </CardContent>
                      </Card>
                    )}
                  </For>
                </Show>
              </section>
            </Show>

            {/* ── Música: setlists ──────────────────────────────────────── */}
            <Show when={hasCap("songs")}>
              <section class="space-y-2" data-testid="group-setlists">
                <div class="flex items-center justify-between">
                  <h3 class="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    {t(msg`Setlists`)}
                  </h3>
                  <Show when={grants.can(scope(), "songs", "write")}>
                    <Button
                      size="sm"
                      variant="ghost"
                      data-testid="setlist-create"
                      onClick={() => setSetlistOpen(true)}
                    >
                      <ListMusic size={14} /> {t(msg`New setlist`)}
                    </Button>
                  </Show>
                </div>
                <For each={mySetlists()}>
                  {(sl) => (
                    <Card data-testid="setlist-item">
                      <CardContent class="space-y-2 py-2.5">
                        <button
                          type="button"
                          class="flex w-full items-center justify-between text-left"
                          onClick={() =>
                            setExpandedSetlist(expandedSetlist() === sl.id ? null : sl.id)
                          }
                        >
                          <span class="text-sm font-medium">{sl.title}</span>
                          <span class="text-xs text-muted-foreground">
                            {(() => {
                              const count = songsOf(sl.id).length;
                              return t(msg`${count} songs`);
                            })()}
                          </span>
                        </button>
                        <Show when={expandedSetlist() === sl.id}>
                          <div class="space-y-1.5 border-t border-border pt-2">
                            <For each={songsOf(sl.id)}>
                              {(ss, index) => (
                                <div
                                  class="flex items-center justify-between gap-2 text-sm"
                                  data-testid="setlist-song"
                                >
                                  <span class="min-w-0 truncate">
                                    {index() + 1}. {songTitleOf(ss.song_id)}
                                    <span class="text-muted-foreground">
                                      {" "}
                                      · {effectiveKey(songKeyOf(ss.song_id), ss.key_override) ?? ""}
                                      <Show when={ss.key_override}> ({t(msg`occasion key`)})</Show>
                                    </span>
                                  </span>
                                  <Show when={grants.can(scope(), "songs", "write")}>
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      aria-label={t(msg`Remove song`)}
                                      onClick={() => void removeSetlistSong(ss.id)}
                                    >
                                      <Trash2 size={12} />
                                    </Button>
                                  </Show>
                                </div>
                              )}
                            </For>
                            <Show when={grants.can(scope(), "songs", "write")}>
                              <div class="flex items-end gap-2 pt-1">
                                <div class="flex-1">
                                  <Select
                                    options={mySongs().map((s) => ({
                                      value: s.id,
                                      label: s.title ?? s.id,
                                    }))}
                                    value={pickSong()}
                                    onChange={setPickSong}
                                    placeholder={t(msg`Add song…`)}
                                    aria-label={t(msg`Add song`)}
                                  />
                                </div>
                                <div class="w-28">
                                  <Select
                                    options={[
                                      { value: "", label: t(msg`Original key`) },
                                      ...MUSICAL_KEYS.map((k) => ({ value: k, label: k })),
                                    ]}
                                    value={pickKey()}
                                    onChange={setPickKey}
                                    aria-label={t(msg`Occasion key`)}
                                  />
                                </div>
                                <Button
                                  size="sm"
                                  data-testid="setlist-song-add"
                                  onClick={() => void addSongToSetlist(sl.id)}
                                >
                                  <Plus size={14} />
                                </Button>
                              </div>
                            </Show>
                          </div>
                        </Show>
                      </CardContent>
                    </Card>
                  )}
                </For>
              </section>
            </Show>

            {/* ── Reuniones del grupo (célula) ──────────────────────────── */}
            <Show when={hasCap("meetings") || hasCap("attendance")}>
              <section class="space-y-2" data-testid="group-meetings">
                <div class="flex items-center justify-between">
                  <h3 class="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    {t(msg`Meetings`)}
                  </h3>
                  <a href={`/meetings?group=${encodeURIComponent(g().id)}`}>
                    <Button size="sm" variant="ghost">
                      <CalendarClock size={14} /> {t(msg`Manage meetings`)}
                    </Button>
                  </a>
                </div>
                <Show
                  when={myMeetings().length > 0}
                  fallback={
                    <p class="text-sm text-muted-foreground">
                      {t(msg`No meetings for this group yet.`)}
                    </p>
                  }
                >
                  <For each={myMeetings()}>
                    {(m) => (
                      <Link to="/meetings/$meetingId" params={{ meetingId: m.id }} class="block">
                        <Card
                          data-testid="group-meeting-item"
                          class="transition-colors hover:border-primary/40"
                        >
                          <CardContent class="flex items-center justify-between gap-3 py-2.5">
                            <p class="min-w-0 truncate text-sm font-medium">{m.title}</p>
                            <p class="shrink-0 text-xs text-muted-foreground">
                              {m.rrule ? t(msg`Recurring`) : t(msg`One-off`)}
                            </p>
                          </CardContent>
                        </Card>
                      </Link>
                    )}
                  </For>
                </Show>
              </section>
            </Show>

            {/* ── Discipulado: devocionales con seguimiento (F4) ─────────── */}
            <Show when={hasCap("devotional-tracking")}>
              <DevotionalSection groupId={props.groupId} church={church()} />
            </Show>

            {/* ── Teatro/danza: obras + elementos de escena (F4) ─────────── */}
            <Show when={hasCap("scene-elements")}>
              <SceneSection
                groupId={props.groupId}
                categoryId={groupType()?.category_id}
                church={church()}
                people={people.data ?? []}
              />
            </Show>

            {/* ── Limpieza/servidores: roster de turnos (F5-residual) ────── */}
            <Show when={hasCap("cleaning-roster")}>
              <CleaningSection
                groupId={props.groupId}
                church={church()}
                people={people.data ?? []}
              />
            </Show>
          </>
        )}
      </Show>

      {/* ── Diálogos ─────────────────────────────────────────────────────── */}
      <FormDialog
        open={memberOpen()}
        onOpenChange={setMemberOpen}
        title={t(msg`Add member`)}
        icon={<UserPlus size={20} />}
        submitTestId="group-member-submit"
        disabled={!memberPerson()}
        onSubmit={addMember}
      >
        <Field id="gm-person" label={t(msg`Person`)}>
          <Select
            options={personOptions()}
            value={memberPerson()}
            onChange={setMemberPerson}
            aria-label={t(msg`Person`)}
            emptyAction={{ label: t(msg`Add people to the directory first`), href: "/people" }}
          />
        </Field>
        <Field
          id="gm-role"
          label={t(msg`Function in the group`)}
          value={memberRole()}
          onInput={setMemberRole}
          placeholder={t(msg`moderator, guitar, voice, host…`)}
          testId="group-member-role"
        />
      </FormDialog>

      <FormDialog
        open={songOpen()}
        onOpenChange={setSongOpen}
        title={t(msg`New song`)}
        icon={<Music size={20} />}
        submitTestId="song-submit"
        disabled={!songTitle().trim()}
        onSubmit={addSong}
      >
        <Field
          id="s-title"
          label={t(msg`Title`)}
          value={songTitle()}
          onInput={setSongTitle}
          testId="song-title-input"
        />
        <div class="grid grid-cols-3 gap-3">
          <Field id="s-key" label={t(msg`Key`)}>
            <Select
              options={MUSICAL_KEYS.map((k) => ({ value: k, label: k }))}
              value={songKey()}
              onChange={setSongKey}
              aria-label={t(msg`Key`)}
            />
          </Field>
          <Field
            id="s-tempo"
            label={t(msg`Tempo (bpm)`)}
            type="number"
            value={songTempo()}
            onInput={setSongTempo}
          />
          <Field
            id="s-author"
            label={t(msg`Author`)}
            value={songAuthor()}
            onInput={setSongAuthor}
          />
        </div>
      </FormDialog>

      <FormDialog
        open={rehearsalOpen()}
        onOpenChange={setRehearsalOpen}
        title={t(msg`New rehearsal`)}
        icon={<CalendarClock size={20} />}
        submitTestId="rehearsal-submit"
        disabled={!rehearsalTitle().trim() || !rehearsalStart()}
        onSubmit={addRehearsal}
      >
        <Field
          id="r-title"
          label={t(msg`Title`)}
          value={rehearsalTitle()}
          onInput={setRehearsalTitle}
          testId="rehearsal-title-input"
        />
        <Field
          id="r-start"
          label={t(msg`Starts`)}
          type="datetime-local"
          value={rehearsalStart()}
          onInput={setRehearsalStart}
          testId="rehearsal-start-input"
        />
        <Field
          id="r-location"
          label={t(msg`Location`)}
          value={rehearsalLocation()}
          onInput={setRehearsalLocation}
        />
      </FormDialog>

      <FormDialog
        open={setlistOpen()}
        onOpenChange={setSetlistOpen}
        title={t(msg`New setlist`)}
        icon={<ListMusic size={20} />}
        submitTestId="setlist-submit"
        disabled={!setlistTitle().trim()}
        onSubmit={addSetlist}
      >
        <Field
          id="sl-title"
          label={t(msg`Title`)}
          value={setlistTitle()}
          onInput={setSetlistTitle}
          testId="setlist-title-input"
        />
      </FormDialog>
    </div>
  );
}
